/**
 * Central configuration for RTFM Demo Client
 *
 * Default values can be overridden at build/runtime using Vite environment variables.
 * Set variables in .env file or via command line:
 *   VITE_WEBSOCKET_PORT=9000 npm run dev
 *
 * See .env.example for all available overrides.
 */

// Helper to read environment variables with type conversion
function env(key, defaultValue) {
  const value = import.meta.env[key];
  if (value === undefined) return defaultValue;

  // Type conversion based on default value type
  if (typeof defaultValue === 'boolean') {
    return value === 'true' || value === '1';
  }
  if (typeof defaultValue === 'number') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? defaultValue : parsed;
  }
  return value;
}

export const CONFIG = {
  // Server Connection (configurable via VITE_* env vars)
  WEBSOCKET_PORT: env('VITE_WEBSOCKET_PORT', 8083), // RTFM port if making a direct connection without gateway
  DIRECT_WS_PATH: "/ws",
  USE_GATEWAY: env('VITE_USE_GATEWAY', false), // if false, client will make direct websocket connection to RTFM
  GATEWAY_HTTP_PORT: env('VITE_GATEWAY_HTTP_PORT', ""),
  GATEWAY_HOSTNAME: env('VITE_GATEWAY_HOSTNAME', ""),
  GATEWAY_REQUEST_SESSION_PATH: "/request-session",
  GATEWAY_SESSION_STATUS_PATH: "/session-status",
  GATEWAY_WS_PATH: "/ws",
  RECONNECT_INITIAL_DELAY: 1000, // ms.
  RECONNECT_MAX_DELAY: 30000, // ms
  METADATA_TIMEOUT: 3000, // ms - fallback to default if server doesn't send metadata.
  HEARTBEAT_INTERVAL_MS: 250,

  // Rendering (configurable via VITE_* env vars)
  DEFAULT_RESOLUTION: {
    width: env('VITE_RESOLUTION_WIDTH', 256),
    height: env('VITE_RESOLUTION_HEIGHT', 256)
  },

  // Camera settings for server
  CAMERA_FOV: 60, // degrees.
  CAMERA_NEAR: 0.1, // meters.
  CAMERA_FAR: 100, // meters.
  CAMERA_INITIAL_POSITION: { x: 0, y: 0, z: 0 },
  PIXEL_RATIO: 1,
  ANTIALIAS: true,
  POWER_PREFERENCE: "high-performance",

  // Network Optimization
  CAMERA_UPDATE_THROTTLE_RATE: 30, // Hz - max updates per second.
  CAMERA_UPDATE_DEBOUNCE_DELAY: 100, // ms - delay after movement stops.

  // Viewport
  DEFAULT_VIEWPORT_MODE: "fit",

  // UI Updates
  FPS_DISPLAY_UPDATE_INTERVAL: 1000, // ms.
  STREAM_FPS_LOG_INTERVAL: 5000, // ms.

  // Keyboard Bindings
  KEYS: {
    // Movement
    FORWARD: "KeyW",
    BACKWARD: "KeyS",
    LEFT: "KeyA",
    RIGHT: "KeyD",
    UP: "KeyE",
    DOWN: "KeyQ",
    ROLL_LEFT: "KeyR",
    ROLL_RIGHT: "KeyF",
    ZOOM: "mouse2",
    FOV: "scrollWheel",

    // UI Controls
    STATS_TOGGLE: "F1", // Show stats.
    MODE_NATIVE: "Digit0", // Native resolution.
    MODE_FIT: "Digit9", // Fit to window.
  },

  // Key bindings mapped to movement state.
  // Shared by the runtime control system to avoid switch statement duplication.
  KEY_TO_MOVEMENT: {
    KeyW: "forward",
    KeyS: "backward",
    KeyA: "left",
    KeyD: "right",
    KeyE: "up",
    KeyQ: "down",
    KeyR: "rollLeft",
    KeyF: "rollRight",
  },

  // Command Console
  CONSOLE_ENABLED: import.meta.env.MODE !== "production", // Master switch - set to false for production.
  CONSOLE_TOGGLE_KEY: "Backquote", // Toggle console.
  CONSOLE_MAX_HISTORY: 100, // Command history limit.
  CONSOLE_MAX_OUTPUT_LINES: 500, // Output buffer limit.
  CONSOLE_COMMAND_TIMEOUT: 5000, // ms - server command timeout.

  // Physics defaults used by certain control surfaces
  PHYSICS: {
    GRAVITY_ACCELERATION: 9.81,
    JUMP_IMPULSE: 3.2,
  },
};
